import { useState } from 'react';
import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DashboardIdle, DashboardIdleGroup } from '../services/responses/dashboard.response';
import { Panel, PanelState, Segmented, useDashboardColors } from './panel';

type Mode = 'owner' | 'column';

interface Props {
    data?: DashboardIdle;
    /// Linha clicada: filtra o painel pelo responsável ou pela etapa.
    onPickOwner: (id: string | null) => void;
    onPickColumn: (id: string, boardId: string) => void;
}

/// Abertos por responsável, pelo tempo sem mexer; as faixas são estado e usam cores de situação.
export const IdleQueue = ({ data, onPickOwner, onPickColumn }: Props) => {
    const { t } = useTranslation();
    const cc = useDashboardColors();
    const [mode, setMode] = useState<Mode>('owner');

    const rows = (mode === 'owner' ? data?.byOwner : data?.byColumn) ?? [];
    const largest = Math.max(1, ...rows.map((row) => row.recent + row.aging + row.stale));

    const bands = data
        ? [
            { key: 'recent' as const, color: cc.accent, label: t('painel.faixaRecente', { dias: data.agingDays }) },
            {
                key: 'aging' as const,
                color: cc.eventWarning,
                label: t('painel.faixaEsfriando', { de: data.agingDays, ate: data.staleDays }),
            },
            { key: 'stale' as const, color: cc.eventDanger, label: t('painel.faixaParado', { dias: data.staleDays }) },
        ]
        : [];

    const pick = (row: DashboardIdleGroup) => {
        if (mode === 'owner') onPickOwner(row.id ?? null);
        else if (row.id && row.boardId) onPickColumn(row.id, row.boardId);
    };

    return (
        <Panel
            title={t('painel.filaTitulo')}
            subtitle={data ? t('painel.filaAjuda', { total: data.open }) : undefined}
            action={
                <Segmented
                    value={mode}
                    options={[
                        { value: 'owner', label: t('painel.responsavel') },
                        { value: 'column', label: t('painel.etapa') },
                    ]}
                    onChange={setMode}
                />
            }
            footer={rows.length > 0 ? t('painel.filaRodape') : undefined}
        >
            {!data || rows.length === 0 ? (
                <PanelState loading={!data} empty={t('painel.filaVazia')} height={240} />
            ) : (
                <>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                        {bands.map((band) => (
                            <Box key={band.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '3px', background: band.color }} />
                                <Typography sx={{ color: cc.textSecondary, fontSize: '0.74rem' }}>{band.label}</Typography>
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, maxHeight: 330, overflowY: 'auto' }}>
                        {rows.map((row) => {
                            const total = row.recent + row.aging + row.stale;

                            return (
                                <Tooltip
                                    key={row.id ?? 'sem-dono'}
                                    arrow
                                    placement="top"
                                    title={bands.map((band) => `${band.label}: ${row[band.key]}`).join(' · ')}
                                >
                                    <ButtonBase
                                        onClick={() => pick(row)}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr auto', sm: '170px 1fr auto' },
                                            alignItems: 'center',
                                            columnGap: 1.5,
                                            rowGap: 0.5,
                                            width: '100%',
                                            py: 0.7,
                                            px: 0.75,
                                            borderRadius: '6px',
                                            textAlign: 'left',
                                            '&:hover': { background: cc.countBg },
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                color: row.id ? cc.textPrimary : cc.textMuted,
                                                fontSize: '0.8rem',
                                                fontWeight: 600,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {row.name}
                                        </Typography>

                                        <Box
                                            sx={{
                                                gridColumn: { xs: '1 / -1', sm: 'auto' },
                                                gridRow: { xs: 2, sm: 'auto' },
                                                display: 'flex',
                                                gap: '2px',
                                                height: 14,
                                                width: `${(total / largest) * 100}%`,
                                                minWidth: 6,
                                                // As pontas arredondam no contêiner; o meio é emenda.
                                                borderRadius: '4px',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {bands.map((band) => row[band.key] > 0 && (
                                                <Box
                                                    key={band.key}
                                                    sx={{
                                                        flex: row[band.key],
                                                        background: band.color,
                                                    }}
                                                />
                                            ))}
                                        </Box>

                                        <Typography
                                            sx={{
                                                color: cc.textPrimary,
                                                fontSize: '0.8rem',
                                                fontWeight: 700,
                                                whiteSpace: 'nowrap',
                                                fontVariantNumeric: 'tabular-nums',
                                                textAlign: 'right',
                                                minWidth: 72,
                                            }}
                                        >
                                            {total}
                                            {row.stale > 0 && (
                                                <Box component="span" sx={{ color: cc.eventDanger, fontWeight: 600, fontSize: '0.72rem' }}>
                                                    {' · '}{t('painel.paradosQtd', { total: row.stale, dias: data.staleDays })}
                                                </Box>
                                            )}
                                        </Typography>
                                    </ButtonBase>
                                </Tooltip>
                            );
                        })}
                    </Box>
                </>
            )}
        </Panel>
    );
};
